import { useEffect, useState } from "react";
import { apiPatch, apiPost } from "./api";
import { withRussianPrefix } from "./phone";
import { Disclosure, Kv } from "./Disclosure";
import { plural } from "./statusLabels";

// Driver card: the name passengers see, the callback phone and the car. The
// driver keeps it current himself; the dispatcher only reviews the first
// application (concept 1.5).
const DriverCard = ({ profile, cityId, refreshMe, onError, onNotice, open, onToggle }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ displayName: "", phone: "", about: "", color: "", plate: "", brand: "" });

  const fill = () =>
    setForm({
      displayName: profile?.displayName ?? "",
      phone: profile?.phone ?? "",
      about: profile?.about ?? "",
      color: profile?.car?.color ?? "",
      plate: profile?.car?.plate ?? "",
      brand: profile?.car?.brand ?? "",
    });

  useEffect(() => {
    if (profile) fill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  if (!profile) return null;

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await apiPatch("/api/drivers/me/profile", {
        cityId,
        displayName: form.displayName.trim(),
        phone: withRussianPrefix(form.phone.trim()),
        about: form.about.trim(),
        car: {
          color: form.color.trim(),
          plate: form.plate.trim(),
          ...(form.brand.trim() && { brand: form.brand.trim() }),
        },
      });
      await refreshMe();
      setEditing(false);
      onNotice?.("Анкета водителя обновлена");
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy(false);
    }
  };

  const rating =
    profile.ratingCount > 0
      ? `★ ${profile.rating?.toFixed(1)} · ${profile.ratingCount} ${plural(profile.ratingCount, "оценка", "оценки", "оценок")}`
      : "нет оценок";

  return (
    <Disclosure
      id="cab-driver"
      icon="driver"
      title="Анкета водителя"
      hint={`${profile.carCaption || "машина не указана"} · ${rating}`}
      open={open}
      onToggle={onToggle}
    >
      {editing ? (
        <form className="flex flex-col gap-2" onSubmit={save}>
          <div>
            <label className="taxi-field-label" htmlFor="drv-name">Имя для пассажиров</label>
            <input
              id="drv-name"
              className="taxi-field"
              required
              value={form.displayName}
              onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            />
          </div>
          <div>
            <label className="taxi-field-label" htmlFor="drv-phone">Телефон</label>
            <input
              id="drv-phone"
              className="taxi-field"
              type="tel"
              inputMode="tel"
              required
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
            />
          </div>
          <div>
            <label className="taxi-field-label" htmlFor="drv-about">О себе</label>
            <input
              id="drv-about"
              className="taxi-field"
              maxLength={160}
              placeholder="Например: стаж 12 лет, езжу аккуратно"
              value={form.about}
              onChange={(event) => setForm({ ...form, about: event.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="taxi-field-label" htmlFor="drv-color">Цвет</label>
              <input
                id="drv-color"
                className="taxi-field"
                required
                value={form.color}
                onChange={(event) => setForm({ ...form, color: event.target.value })}
              />
            </div>
            <div className="w-28">
              <label className="taxi-field-label" htmlFor="drv-plate">Номер</label>
              <input
                id="drv-plate"
                className="taxi-field"
                required
                value={form.plate}
                onChange={(event) => setForm({ ...form, plate: event.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="taxi-field-label" htmlFor="drv-brand">Марка</label>
            <input
              id="drv-brand"
              className="taxi-field"
              value={form.brand}
              onChange={(event) => setForm({ ...form, brand: event.target.value })}
            />
          </div>
          <div className="taxi-acc-actions">
            <button
              type="button"
              className="taxi-btn flex-1"
              onClick={() => {
                fill();
                setEditing(false);
              }}
            >
              Отмена
            </button>
            <button type="submit" className="taxi-btn is-accent flex-1" disabled={busy}>
              {busy ? "Сохраняем…" : "Сохранить"}
            </button>
          </div>
        </form>
      ) : (
        <>
          <Kv label="Имя для пассажиров" value={profile.displayName} />
          <Kv label="Телефон" value={profile.phone} />
          <Kv label="Машина" value={profile.carCaption} />
          <Kv label="О себе" value={profile.about} />
          <Kv label="Рейтинг" value={rating} />
          <div className="taxi-acc-actions">
            <button
              type="button"
              className="taxi-btn"
              onClick={() => {
                fill();
                setEditing(true);
              }}
            >
              Изменить
            </button>
          </div>
        </>
      )}
    </Disclosure>
  );
};

// Leaving a role: the person stops being a driver here. Refused while an
// active ride or a dispatcher's hold keeps him attached. Lives inside the
// "Мои роли" section, so the way out sits next to the role itself.
const LeaveRoleButtons = ({ memberships, refreshMe, onError, onNotice, onConfirm }) => {
  const left = memberships
    .filter((membership) => membership.status === "ACTIVE")
    .map((membership) => membership.role)
    .filter((role) => role !== "dispatcher" && role !== "region_admin");
  if (left.length === 0) return null;

  const leave = async (membership) => {
    const ok = await onConfirm?.({
      title: "Выйти из роли?",
      text: "Роль исчезнет из кабинета. Вернуться можно новой заявкой.",
      okText: "Выйти",
      danger: true,
    });
    if (!ok) return;
    try {
      await apiPost(`/api/memberships/${membership._id}`, { leave: true });
      await refreshMe();
      onNotice?.("Вы вышли из роли");
    } catch (err) {
      onError?.(err.message);
    }
  };

  return (
    <div className="taxi-acc-actions">
      {memberships
        .filter((membership) => membership.status === "ACTIVE" && left.includes(membership.role))
        .map((membership) => (
          <button
            key={membership._id}
            className="taxi-btn is-danger"
            onClick={() => leave(membership)}
          >
            Перестать быть {membership.role === "driver" ? "водителем" : "пассажиром"}
          </button>
        ))}
    </div>
  );
};

export { DriverCard, LeaveRoleButtons };
export default DriverCard;
